import { friendlyError } from "../../../lib/friendlyError";
import { trpc } from "../../../trpc";
import { TestThenSend } from "../TestThenSend";
import {
  EMAIL_OFF_SENTENCE,
  people,
  SEND_OFFLINE_SENTENCE,
  sendAllSentence,
} from "../sendCopy";
import { BODY_TEXT } from "../ui";
import { useSendFlow } from "../useSendFlow";

/**
 * Invite past guests: everyone who has booked for any of our events,
 * except people already booked for this one, invited to this event. It
 * goes out with an unsubscribe link, and anyone who has unsubscribed is
 * left out by the email service. Australia only on the backend.
 */
export function PastGuestsPanel({
  eventSlug,
  eventTitle,
  audienceCount,
  defaultTestEmail,
  senderName,
  alreadySentNote,
}: {
  eventSlug: string;
  eventTitle: string;
  audienceCount: number;
  defaultTestEmail: string;
  senderName: string | null;
  alreadySentNote: string | null;
}) {
  const flow = useSendFlow(defaultTestEmail);
  const utils = trpc.useUtils();
  const broadcast = trpc.adminSendGuestBroadcast.useMutation();

  const send = (mode: "test" | "all") => {
    broadcast.mutate(
      {
        eventSlug,
        mode,
        testEmail: mode === "test" ? flow.testAddress.trim() : undefined,
        testName: mode === "test" ? (senderName ?? undefined) : undefined,
      },
      {
        onSuccess: (res) => {
          if (res.mode === "test") {
            flow.testSent(
              res.skipped
                ? EMAIL_OFF_SENTENCE
                : `Test sent to ${res.sentTo}. Check the invitation, and that the unsubscribe link is at the bottom, then send to past guests.`,
            );
          } else {
            flow.sendAllDone(
              sendAllSentence(
                res,
                "There are no past guests to invite, so nothing was sent.",
              ),
            );
            void utils.adminListEmailSends.invalidate();
          }
        },
        onError: (err) =>
          flow.problem(friendlyError(err, SEND_OFFLINE_SENTENCE)),
      },
    );
  };

  return (
    <>
      <p className={BODY_TEXT}>
        An invitation to people who have come to one of our events before but
        haven't booked for {eventTitle}. Anyone who has unsubscribed won't get
        it.
      </p>
      <TestThenSend
        flow={flow}
        emailName="Invite past guests"
        audience={audienceCount > 0 ? people(audienceCount) : null}
        noAudienceText="There are no past guests to invite yet."
        busy={broadcast.isPending}
        onSendTest={() => send("test")}
        onSendAll={() => send("all")}
        alreadySentNote={alreadySentNote}
      />
    </>
  );
}
